import { useState, useEffect, useCallback } from 'react';
import { api, HostAccommodationType, PendingHost } from '../api/client';
import ConfirmModal from '../components/ConfirmModal';
import ToastStack from '../components/Toast';
import { useToast } from '../hooks/useToast';

// Mirrors HostAccommodationType on the server. A value we don't know about
// falls back to the raw string rather than disappearing — a moderator seeing
// `farm_stay` learns more than one seeing a blank cell.
const ACCOMMODATION_LABELS: Record<HostAccommodationType, string> = {
  hotel: 'Hôtellerie',
  short_term_rental: 'Location courte durée',
  bnb: "Gîte / chambre d'hôtes",
  camping: 'Camping',
  chalet: 'Chalet',
  other: 'Autre',
};

function accommodationLabel(type: HostAccommodationType | null): string | null {
  if (!type) return null;
  return ACCOMMODATION_LABELS[type] ?? type;
}

/** Shows the domain in the cell; the full URL stays in href and title. */
function listingDisplay(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function formatDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Date inconnue';

  return new Intl.DateTimeFormat('fr-CA', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(date);
}

function hostDisplayName(host: PendingHost): string {
  return host.display_name?.trim() || 'Nom non renseigné';
}

function initials(name: string | null): string {
  return (name?.trim() || '?')
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word.charAt(0))
    .join('')
    .toUpperCase();
}

const AVATAR_COLORS = [
  '#5b4fcf', '#2563eb', '#0891b2', '#059669',
  '#d97706', '#dc2626', '#7c3aed', '#db2777',
];

function avatarColor(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffffffff;
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

export default function Hosts({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const [hosts, setHosts] = useState<PendingHost[]>([]);
  const [loading, setLoading] = useState(true);
  const [confirming, setConfirming] = useState<PendingHost | null>(null);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const { toasts, push, dismiss } = useToast();

  const load = useCallback(() => {
    setLoading(true);
    api
      .getPendingHosts()
      .then((r) => {
        setHosts(r.data);
        onCountChange?.(r.data.length);
      })
      .catch(() => push('Impossible de charger les demandes.', 'error'))
      .finally(() => setLoading(false));
  }, [onCountChange, push]);

  useEffect(() => { load(); }, [load]);

  async function validate(host: PendingHost) {
    setActionLoading(host.id);
    try {
      await api.validateHost(host.id);
      push(`${hostDisplayName(host)} validé·e comme hôte.`, 'success');
      setHosts((prev) => prev.filter((h) => h.id !== host.id));
      onCountChange?.(hosts.length - 1);
    } catch (err: unknown) {
      push(err instanceof Error ? err.message : 'Erreur.', 'error');
    } finally {
      setActionLoading(null);
      setConfirming(null);
    }
  }

  if (loading) {
    return <div className="loading"><div className="spinner" /></div>;
  }

  return (
    <>
      <ToastStack toasts={toasts} dismiss={dismiss} />

      <div className="section-head">
        <div>
          <div className="section-title">Demandes d'activation Hôte</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            Utilisateurs ayant demandé le rôle Hôte — validez ou ignorez.
          </div>
        </div>
        <div className="section-count">
          {hosts.length} demande{hosts.length !== 1 ? 's' : ''}
        </div>
      </div>

      {hosts.length === 0 ? (
        <div className="empty">
          <div className="empty-icon">✓</div>
          <div className="empty-title">Aucune demande en attente</div>
          <div className="empty-sub">Toutes les demandes ont été traitées.</div>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="host-table">
            <thead>
              <tr>
                <th>Utilisateur</th>
                <th>Email</th>
                <th>Hébergement</th>
                <th>Demandé le</th>
                <th style={{ textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {hosts.map((host) => (
                <tr key={host.id}>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div
                        style={{
                          width: 34,
                          height: 34,
                          borderRadius: '50%',
                          background: avatarColor(host.id),
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: 12,
                          fontWeight: 700,
                          color: '#fff',
                          flexShrink: 0,
                        }}
                      >
                        {initials(host.display_name)}
                      </div>
                      <div>
                        <div className="host-name">{hostDisplayName(host)}</div>
                        <div style={{ fontSize: 10, color: 'var(--text-dim)', fontFamily: 'monospace' }}>
                          {host.id.slice(0, 8)}…
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="host-email">{host.email}</div>
                  </td>
                  <td>
                    {accommodationLabel(host.accommodation_type) ? (
                      <div className="host-type">
                        {accommodationLabel(host.accommodation_type)}
                      </div>
                    ) : (
                      <div className="host-type host-type-missing">
                        Non précisé
                      </div>
                    )}
                    {host.listing_url ? (
                      <a
                        className="host-listing"
                        href={host.listing_url}
                        title={host.listing_url}
                        target="_blank"
                        // The URL is applicant-supplied: noreferrer keeps our
                        // panel out of their referer logs, noopener keeps the
                        // opened page away from this window.
                        rel="noopener noreferrer"
                      >
                        {listingDisplay(host.listing_url)} ↗
                      </a>
                    ) : (
                      <div className="host-listing host-type-missing">
                        Aucun lien
                      </div>
                    )}
                  </td>
                  <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>
                    {formatDate(host.requested_at)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      className="btn btn-success btn-sm"
                      disabled={actionLoading === host.id}
                      onClick={() => setConfirming(host)}
                    >
                      {actionLoading === host.id ? (
                        <span className="spinner" />
                      ) : (
                        '✓'
                      )}
                      Valider
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {confirming && (
        <ConfirmModal
          title="Valider le rôle Hôte"
          description={`${hostDisplayName(confirming)} (${confirming.email}) — ${
            accommodationLabel(confirming.accommodation_type) ??
            'type d’hébergement non précisé'
          }. Cette personne pourra publier des offres sur Staynow et recevra un courriel de bienvenue. Cette validation est définitive.`}
          confirmLabel="Valider l'hôte"
          confirmVariant="success"
          onConfirm={() => validate(confirming)}
          onCancel={() => setConfirming(null)}
          loading={actionLoading === confirming.id}
        />
      )}
    </>
  );
}
